import { useState, useEffect } from "react";
import { casualData } from "../api/casualData.js";
import { EditableDataTable } from "./EditableDataTable.jsx";
import { useRealtime } from "./realtime/RealtimeProvider.jsx";

// Bendras sąrašo rodinys, naudojamas abiejuose lygiuose (gamintojai →
// grupės): pavadinimas, pervadinimas (pieštukas + inline laukelis,
// Enter=išsaugoti/Escape=atšaukti — ta pati logika kaip kitur programoje),
// atidarymas, šalinimas ir naujo įrašo pridėjimas.
function EntityList({ items, onOpen, onDelete, onRename, onAdd, addLabel, emptyLabel, subtitle }) {
  const [renamingId, setRenamingId] = useState(null);
  const [renameVal, setRenameVal] = useState("");
  const [showAdd, setShowAdd] = useState(false);
  const [newName, setNewName] = useState("");

  function saveRename(id) {
    const val = renameVal.trim();
    if (val) onRename(id, val);
    setRenamingId(null);
  }
  function submitAdd() {
    const val = newName.trim();
    if (!val) return;
    onAdd(val);
    setNewName("");
    setShowAdd(false);
  }

  return (
    <div>
      {items.length === 0 && <p style={{ fontSize: 13, color: "#9ca3af", marginBottom: 8 }}>{emptyLabel}</p>}
      <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 12 }}>
        {items.map(item => (
          <div key={item.id} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", border: "1px solid #e5e7eb", borderRadius: 10, padding: "10px 14px" }}>
            {renamingId === item.id ? (
              <input autoFocus value={renameVal} onChange={e => setRenameVal(e.target.value)}
                onKeyDown={e => { if (e.key === "Enter") saveRename(item.id); if (e.key === "Escape") setRenamingId(null); }}
                style={{ flex: 1, border: "1px solid #e5e7eb", borderRadius: 6, padding: "5px 8px", fontSize: 13, fontWeight: 700, marginRight: 10, boxSizing: "border-box" }} />
            ) : (
              <div>
                <div style={{ fontSize: 14, fontWeight: 700, color: "#1c1c1c" }}>{item.name || "(be pavadinimo)"}</div>
                {subtitle && <div style={{ fontSize: 11, color: "#9ca3af" }}>{subtitle(item)}</div>}
              </div>
            )}
            <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
              {renamingId !== item.id && (
                <button onClick={() => { setRenamingId(item.id); setRenameVal(item.name || ""); }} title="Pervadinti"
                  style={{ background: "none", border: "none", color: "#9ca3af", cursor: "pointer", fontSize: 13 }}>✏</button>
              )}
              <button onClick={() => onOpen(item.id)} style={{ background: "#f3f4f6", color: "#383853", border: "none", borderRadius: 6, padding: "6px 12px", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>
                Atidaryti
              </button>
              <button onClick={() => { if (window.confirm("Ištrinti?")) onDelete(item.id); }} style={{ background: "none", border: "none", color: "#d1d5db", cursor: "pointer", fontSize: 16 }}>×</button>
            </div>
          </div>
        ))}
      </div>
      {showAdd ? (
        <div style={{ display: "flex", gap: 8 }}>
          <input autoFocus value={newName} onChange={e => setNewName(e.target.value)}
            onKeyDown={e => { if (e.key === "Enter") submitAdd(); if (e.key === "Escape") { setShowAdd(false); setNewName(""); } }}
            placeholder="Pavadinimas..."
            style={{ flex: 1, border: "1px solid #e5e7eb", borderRadius: 8, padding: "8px 10px", fontSize: 13, boxSizing: "border-box" }} />
          <button onClick={submitAdd} style={{ background: "#735DFF", color: "#fff", border: "none", borderRadius: 8, padding: "8px 16px", fontSize: 13, fontWeight: 700, cursor: "pointer" }}>Pridėti</button>
          <button onClick={() => { setShowAdd(false); setNewName(""); }} style={{ background: "#f3f4f6", color: "#383853", border: "none", borderRadius: 8, padding: "8px 12px", fontSize: 13, cursor: "pointer" }}>Atšaukti</button>
        </div>
      ) : (
        <button onClick={() => setShowAdd(true)} style={{ background: "#735DFF", color: "#fff", border: "none", borderRadius: 8, padding: "8px 14px", fontSize: 13, fontWeight: 700, cursor: "pointer" }}>
          {addLabel}
        </button>
      )}
    </div>
  );
}

const backButtonStyle = { background: "#f3f4f6", color: "#383853", border: "none", borderRadius: 8, padding: "8px 12px", fontSize: 13, fontWeight: 600, cursor: "pointer" };

// Perkrovus puslapį (F5), skiltis vėl atsidaro ten pat, kur vartotojas dirbo
// (tas pats gamintojas/grupė).
const NAV_KEY = "sannamai_kainininkas_nav";
function readStoredNav() {
  try {
    const parsed = JSON.parse(localStorage.getItem(NAV_KEY) || "null");
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch { return {}; }
}
function storeNav(nav) {
  try { localStorage.setItem(NAV_KEY, JSON.stringify(nav)); } catch {}
}

// Kainininkas = Gamintojas → Medžiagų grupė → viena lentelė (kainininkas),
// redaguojama tuo pačiu EditableDataTable, kaip ir Duomenų matrica (su
// showSuppliers={true}, kad kiekviena eilutė galėtų turėti savo tiekėjo
// įrašus — žr. EditableDataTable.jsx). Kiekvienas sukūrimas/pervadinimas/
// šalinimas yra SAVO granuliuotas REST kvietimas (optimistinis vietinis
// atnaujinimas + patikslinimas serverio atsakymu arba grąžinimas atgal
// klaidos atveju) — ne visos struktūros PUT, todėl nesusijusių šakų
// redagavimas skirtingose sesijose niekada nesusiduria.
export function KainininkasMasterView() {
  const [manufacturers, setManufacturers] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [manufacturerId, setManufacturerId] = useState(() => readStoredNav().manufacturerId ?? null);
  const [groupId, setGroupId] = useState(() => readStoredNav().groupId ?? null);
  const realtime = useRealtime();

  useEffect(() => {
    storeNav({ manufacturerId, groupId });
  }, [manufacturerId, groupId]);

  useEffect(() => {
    let cancelled = false;
    casualData.getKainininkasTree()
      .then(data => { if (!cancelled) setManufacturers(Array.isArray(data) ? data : []); })
      .catch(e => console.error("Failed to load kainininkas tree", e))
      .finally(() => { if (!cancelled) setLoaded(true); });
    return () => { cancelled = true; };
  }, []);

  // Gyvi pakeitimai iš kitų sesijų (kitos master paskyros ar kito skirtuko) —
  // pritaikomi vietoje tiesiai į medį, be perkrovimo.
  useEffect(() => {
    if (!realtime) return;
    return realtime.subscribe(msg => {
      if (msg.type !== "entity") return;
      if (msg.entity === "manufacturer") {
        if (msg.action === "created") setManufacturers(prev => [...prev, { ...msg.manufacturer, groups: [] }]);
        if (msg.action === "updated") setManufacturers(prev => prev.map(m => m.id === msg.manufacturer.id ? { ...m, ...msg.manufacturer } : m));
        if (msg.action === "deleted") setManufacturers(prev => prev.filter(m => m.id !== msg.id));
      }
      if (msg.entity === "group") {
        if (msg.action === "created") setManufacturers(prev => prev.map(m => m.id === msg.group.manufacturerId ? { ...m, groups: [...m.groups, msg.group] } : m));
        if (msg.action === "updated") setManufacturers(prev => prev.map(m => ({ ...m, groups: m.groups.map(g => g.id === msg.group.id ? { ...g, ...msg.group } : g) })));
        if (msg.action === "deleted") setManufacturers(prev => prev.map(m => ({ ...m, groups: m.groups.filter(g => g.id !== msg.id) })));
      }
    });
  }, [realtime]);

  const manufacturer = manufacturers.find(m => m.id === manufacturerId) || null;
  const group = manufacturer?.groups.find(g => g.id === groupId) || null;

  // EditableDataTable stulpelių/eilučių keitimas atnaujina TIK savo vietinį
  // state'ą — realtime transliacija praleidžia pačią siunčiančią sesiją (žr.
  // registry.ts broadcast), tad be šio perkrovimo "X stulpeliai" užrašas
  // liktų pasenęs grįžus iš lentelės po stulpelio pridėjimo/pašalinimo.
  async function backToGroup() {
    setGroupId(null);
    try {
      const fresh = await casualData.getKainininkasTree();
      setManufacturers(Array.isArray(fresh) ? fresh : []);
    } catch (e) { console.error("Failed to refresh kainininkas tree", e); }
  }

  // "Naudoti šabloną" sąrašas grupės lentelei — visos grupių lentelės su bent 1 stulpeliu.
  const allGroupTemplates = manufacturers.flatMap(m => m.groups.map(g => ({ id: g.tableId, name: g.name, columnCount: g.tableColumnCount })));

  async function addManufacturer(name) {
    try {
      const created = await casualData.createManufacturer(name);
      setManufacturers(prev => prev.some(m => m.id === created.id) ? prev : [...prev, { ...created, groups: [] }]);
    } catch (e) { console.error("Failed to create manufacturer", e); }
  }
  async function renameManufacturer(id, name) {
    const target = manufacturers.find(m => m.id === id);
    if (!target) return;
    setManufacturers(prev => prev.map(m => m.id === id ? { ...m, name } : m));
    try {
      const updated = await casualData.renameManufacturer(id, name, target.version);
      setManufacturers(prev => prev.map(m => m.id === id ? { ...m, ...updated } : m));
    } catch (e) {
      if (e?.status === 409) setManufacturers(prev => prev.map(m => m.id === id ? { ...m, ...e.body.entity } : m));
      else console.error("Failed to rename manufacturer", e);
    }
  }
  async function deleteManufacturer(id) {
    const target = manufacturers.find(m => m.id === id);
    if (!target) return;
    setManufacturers(prev => prev.filter(m => m.id !== id));
    if (manufacturerId === id) { setManufacturerId(null); setGroupId(null); }
    try { await casualData.deleteManufacturer(id, target.version); }
    catch (e) { console.error("Failed to delete manufacturer", e); }
  }

  async function addGroup(name) {
    try {
      const created = await casualData.createGroup(manufacturerId, name);
      setManufacturers(prev => prev.map(m => m.id === manufacturerId ? { ...m, groups: [...m.groups, created] } : m));
    } catch (e) { console.error("Failed to create group", e); }
  }
  async function renameGroup(id, name) {
    const target = manufacturer?.groups.find(g => g.id === id);
    if (!target) return;
    setManufacturers(prev => prev.map(m => ({ ...m, groups: m.groups.map(g => g.id === id ? { ...g, name } : g) })));
    try {
      const updated = await casualData.renameGroup(id, name, target.version);
      setManufacturers(prev => prev.map(m => ({ ...m, groups: m.groups.map(g => g.id === id ? { ...g, ...updated } : g) })));
    } catch (e) {
      if (e?.status === 409) setManufacturers(prev => prev.map(m => ({ ...m, groups: m.groups.map(g => g.id === id ? { ...g, ...e.body.entity } : g) })));
      else console.error("Failed to rename group", e);
    }
  }
  async function deleteGroup(id) {
    const target = manufacturer?.groups.find(g => g.id === id);
    if (!target) return;
    setManufacturers(prev => prev.map(m => ({ ...m, groups: m.groups.filter(g => g.id !== id) })));
    if (groupId === id) setGroupId(null);
    try { await casualData.deleteGroup(id, target.version); }
    catch (e) { console.error("Failed to delete group", e); }
  }

  if (!loaded) return <p style={{ fontSize: 14, color: "#383853" }}>Kraunama...</p>;

  // Grupės kainininko lentelė
  if (group) {
    return <EditableDataTable tableId={group.tableId} onBack={backToGroup} templates={allGroupTemplates} showSuppliers />;
  }

  // Gamintojo grupės
  if (manufacturer) {
    return (
      <div>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16 }}>
          <button onClick={() => setManufacturerId(null)} style={backButtonStyle}>← Atgal</button>
          <h1 style={{ fontSize: 16, fontWeight: 700, color: "#1C1C1C" }}>{manufacturer.name}</h1>
        </div>
        <EntityList items={manufacturer.groups} onOpen={setGroupId} onDelete={deleteGroup} onRename={renameGroup}
          onAdd={addGroup} addLabel="+ Nauja grupė" emptyLabel="Grupių dar nėra."
          subtitle={g => `${g.tableColumnCount ?? 0} stulpeliai`} />
      </div>
    );
  }

  // Gamintojai
  return (
    <div>
      <h1 style={{ fontSize: 18, fontWeight: 700, color: "#1C1C1C", marginBottom: 16 }}>Kainininkas</h1>
      <EntityList items={manufacturers} onOpen={setManufacturerId} onDelete={deleteManufacturer} onRename={renameManufacturer}
        onAdd={addManufacturer} addLabel="+ Naujas gamintojas" emptyLabel="Gamintojų dar nėra."
        subtitle={m => `${m.groups.length} grupės`} />
    </div>
  );
}
